import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './billing-page.css';
import NavigationV2 from '../../components/v2/NavigationV2.jsx';
import SubscriptionCard from './components/SubscriptionCard.jsx';
import InvoicesTable from './components/InvoicesTable.jsx';
import {
  getCampusId, getCampusList, updateSessionCampus,
} from '../../services/session.js';
import { fetchSubscriptions, fetchInvoices } from './api/billingApi.js';
import { exportInvoicesCsv } from '../../services/csvExport.js';

// ---------------------------------------------------------------------------
// Currency formatter
// ---------------------------------------------------------------------------
const currencyFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

// ---------------------------------------------------------------------------
// Subscription status categories
// ---------------------------------------------------------------------------
const ACTIVE_STATUSES = new Set(['Live', 'Trial', 'Future', 'Non Renewing']);

/** Extract plan type from plan_name (first segment before "|", trimmed). */
function getPlanType(planName) {
  return (planName || '').split('|')[0].trim();
}

/** Parse a date string; returns 0 for invalid / empty dates. */
function dateMs(str) {
  if (!str || str === '0000-00-00 00:00:00') return 0;
  const ms = new Date(str).getTime();
  return isNaN(ms) ? 0 : ms;
}

/**
 * Apply business rules to determine which subscriptions to display.
 *
 *  1. Always show ALL active subscriptions.
 *  2. For non-active subscriptions, only show if no active subscription
 *     shares the same plan type.
 *  3. Dedupe remaining non-active by plan type — keep the one with the
 *     most recent cancel_date.
 *  4. Active subscriptions first, then non-active.
 */
function filterSubscriptions(rawSubs) {
  const active = [];
  const nonActive = [];

  for (const sub of rawSubs) {
    if (ACTIVE_STATUSES.has(sub.subscription_status)) {
      active.push(sub);
    } else {
      nonActive.push(sub);
    }
  }

  // Collect plan types that have at least one active subscription
  const activePlanTypes = new Set(active.map((s) => getPlanType(s.plan_name)));

  // Filter non-active: exclude plan types that have an active counterpart,
  // then dedupe by plan type keeping the most recent cancel_date.
  const bestByType = new Map();
  for (const sub of nonActive) {
    const type = getPlanType(sub.plan_name);
    if (activePlanTypes.has(type)) continue; // rule 2
    const existing = bestByType.get(type);
    if (!existing || dateMs(sub.cancel_date) > dateMs(existing.cancel_date)) {
      bestByType.set(type, sub); // rule 3
    }
  }

  return [...active, ...bestByType.values()];
}

// ---------------------------------------------------------------------------
// Skeleton component
// ---------------------------------------------------------------------------
const SubscriptionSkeleton = () => (
  <div className="bp-sub-card">
    <div className="bp-sub-card__header">
      <div className="bp-skeleton-block" style={{ height: '1.4rem', width: '60%' }} />
      <div className="bp-skeleton-block" style={{ height: '1.4rem', width: '5rem' }} />
    </div>
    <div className="bp-sub-card__grid">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bp-sub-card__item">
          <div className="bp-skeleton-block" style={{ height: '0.7rem', width: '5rem', marginBottom: '6px' }} />
          <div className="bp-skeleton-block" style={{ height: '1rem', width: '8rem' }} />
        </div>
      ))}
    </div>
  </div>
);

const BillingSkeleton = () => (
  <div className="bp-skeleton">
    {/* Two skeleton subscription cards */}
    <div className="bp-subs-grid">
      <SubscriptionSkeleton />
      <SubscriptionSkeleton />
    </div>
    {/* Skeleton total due banner */}
    <div className="bp-skeleton-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
        <div className="bp-skeleton-block" style={{ height: '0.85rem', width: '5rem' }} />
        <div className="bp-skeleton-block" style={{ height: '1.3rem', width: '7rem' }} />
      </div>
    </div>
    {/* Skeleton table rows */}
    <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="bp-skeleton-row">
          {[1, 2, 3, 4, 5, 6].map((j) => (
            <div key={j} className="bp-skeleton-block" style={{ height: '1rem', width: `${40 + j * 12}px`, flex: 1 }} />
          ))}
        </div>
      ))}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// BillingPage
// ---------------------------------------------------------------------------

const BillingPage = () => {
  // ==========================================================================
  // SESSION
  // ==========================================================================
  const [campusId, setCampusId] = useState(() => getCampusId());
  const campusList = useMemo(() => getCampusList() || [], []);
  const campusName = useMemo(() => {
    const match = campusList.find((c) => c.campus_id === campusId);
    return match?.campus_name || '';
  }, [campusId, campusList]);

  // ==========================================================================
  // DATA STATE
  // ==========================================================================
  const [subscriptions, setSubscriptions] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // ==========================================================================
  // SORT STATE
  // ==========================================================================
  const [sortColumn, setSortColumn] = useState('invoice_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // ==========================================================================
  // TOAST
  // ==========================================================================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((type, message) => {
    clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // ==========================================================================
  // DATA LOADING
  // ==========================================================================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, invs] = await Promise.all([
        fetchSubscriptions(),
        fetchInvoices(),
      ]);
      setSubscriptions(subs || []);
      setInvoices(invs || []);
    } catch (err) {
      console.error('[BillingPage] Load error:', err);
      showToast('error', 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // ==========================================================================
  // CAMPUS CHANGE
  // ==========================================================================
  const handleCampusChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setCampusId(val);
    updateSessionCampus(val);
  }, []);

  // Reload data when campus changes
  useEffect(() => { loadData(); }, [campusId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================================================
  // SUBSCRIPTION VIEW TOGGLE
  // ==========================================================================
  const [showAllSubs, setShowAllSubs] = useState(false);

  // Full filtered set (business rules applied — active + eligible non-active)
  const allFilteredSubs = useMemo(
    () => filterSubscriptions(subscriptions),
    [subscriptions],
  );

  // Active-only subset
  const activeOnlySubs = useMemo(
    () => allFilteredSubs.filter((s) => ACTIVE_STATUSES.has(s.subscription_status)),
    [allFilteredSubs],
  );

  // Whether there are non-active subs worth toggling to
  const hasNonActiveSubs = allFilteredSubs.length > activeOnlySubs.length;

  // The list currently shown
  const displaySubscriptions = showAllSubs ? allFilteredSubs : activeOnlySubs;

  // ==========================================================================
  // COMPUTED VALUES
  // ==========================================================================
  const totalDue = useMemo(() => {
    return invoices.reduce((sum, inv) => sum + (parseFloat(inv.balance) || 0), 0);
  }, [invoices]);

  const hasOverdue = useMemo(() => {
    return invoices.some((inv) => inv.status === 'Overdue');
  }, [invoices]);

  // ==========================================================================
  // SORTED INVOICES
  // ==========================================================================
  const sortedInvoices = useMemo(() => {
    const sorted = [...invoices].sort((a, b) => {
      let aVal = a[sortColumn];
      let bVal = b[sortColumn];

      if (['total', 'balance'].includes(sortColumn)) {
        const diff = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
        return sortOrder === 'asc' ? diff : -diff;
      }

      const cmp = String(aVal || '').localeCompare(String(bVal || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [invoices, sortColumn, sortOrder]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================
  const handleSort = useCallback((key) => {
    setSortColumn((prev) => {
      if (prev === key) {
        setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return key;
    });
  }, []);

  const handleExport = useCallback(() => {
    exportInvoicesCsv(invoices);
  }, [invoices]);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  return (
    <>
      <NavigationV2 />
      <div className="nav2-main">
        <div className="bp-page">
          {/* Toast */}
          {toast ? (
            <div className={`bp-toast bp-toast--${toast.type}`} role="status" aria-live="polite">
              <span>{toast.message}</span>
              <button onClick={() => setToast(null)} className="bp-toast__close" aria-label="Dismiss">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ) : null}

          {/* Page Header */}
          <div className="bp-page-header">
            <div className="bp-page-header__inner">
              <div className="bp-breadcrumb-row">
                <div className="bp-breadcrumb">
                  <span className="bp-breadcrumb__text">Administration</span>
                  <svg className="bp-icon-md bp-breadcrumb__separator" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                  <span className="bp-breadcrumb__current">Billing</span>
                </div>
                <div className="bp-campus-row">
                  <label htmlFor="bp-campus-selector" className="bp-campus-label">Campus:</label>
                  {campusList.length > 1 ? (
                    <select
                      id="bp-campus-selector"
                      value={campusId}
                      onChange={handleCampusChange}
                      className="bp-campus-select"
                    >
                      {campusList.map((c) => (
                        <option key={c.campus_id || c.CampusId} value={c.campus_id || c.CampusId}>
                          {c.campus_name || c.CampusName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className="bp-campus-name">{campusName}</span>
                  )}
                </div>
              </div>
              <h1 className="bp-page-title">Billing</h1>
            </div>
          </div>

          {/* Content */}
          <div className="bp-content">
            {loading ? (
              <BillingSkeleton />
            ) : (
              <>
                {/* Subscriptions Section */}
                <div className="bp-subs-section">
                  <div className="bp-subs-header">
                    <h2 className="bp-section-title">
                      Subscriptions
                      {displaySubscriptions.length > 0 ? (
                        <span className="bp-section-count">{displaySubscriptions.length}</span>
                      ) : null}
                    </h2>
                    {hasNonActiveSubs ? (
                      <div className="bp-toggle" role="radiogroup" aria-label="Subscription filter">
                        <button
                          className={`bp-toggle__btn${!showAllSubs ? ' bp-toggle__btn--active' : ''}`}
                          onClick={() => setShowAllSubs(false)}
                          role="radio"
                          aria-checked={!showAllSubs}
                        >
                          Active
                        </button>
                        <button
                          className={`bp-toggle__btn${showAllSubs ? ' bp-toggle__btn--active' : ''}`}
                          onClick={() => setShowAllSubs(true)}
                          role="radio"
                          aria-checked={showAllSubs}
                        >
                          All
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {displaySubscriptions.length === 0 ? (
                    <div className="bp-sub-card">
                      <div className="bp-empty">
                        <svg className="bp-empty__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
                        </svg>
                        <p className="bp-empty__text">No subscription data available for this campus.</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`bp-subs-grid${displaySubscriptions.length === 1 ? ' bp-subs-grid--single' : ''}`}>
                      {displaySubscriptions.map((sub) => (
                        <SubscriptionCard
                          key={sub.subscription_number}
                          subscription={sub}
                          isActive={ACTIVE_STATUSES.has(sub.subscription_status)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Total Due Banner */}
                <div className={`bp-total-banner${hasOverdue ? ' bp-total-banner--overdue' : ''}`}>
                  <span className="bp-total-banner__label">Total Due</span>
                  <span className="bp-total-banner__amount">{currencyFmt.format(totalDue)}</span>
                </div>

                {/* Invoices Section */}
                <div className="bp-invoices-section">
                  <div className="bp-invoices-header">
                    <h2 className="bp-section-title">
                      Invoices
                      {invoices.length > 0 ? (
                        <span className="bp-section-count">{invoices.length}</span>
                      ) : null}
                    </h2>
                    {invoices.length > 0 ? (
                      <button onClick={handleExport} className="bp-export-btn" title="Export invoices to CSV">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Export CSV
                      </button>
                    ) : null}
                  </div>

                  {invoices.length === 0 ? (
                    <div className="bp-empty">
                      <svg className="bp-empty__icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                      </svg>
                      <p className="bp-empty__text">No invoices found for this campus.</p>
                    </div>
                  ) : (
                    <InvoicesTable
                      invoices={sortedInvoices}
                      sortColumn={sortColumn}
                      sortOrder={sortOrder}
                      onSort={handleSort}
                    />
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BillingPage;
